import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Switch,
    FormControlLabel,
    Divider,
    Alert,
    Snackbar,
    CircularProgress,
    Stack,
} from '@mui/material';
import {
    Email as EmailIcon,
    Assessment as AssessmentIcon,
    Person as PersonIcon,
    School as SchoolIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useFetch from '../../../../hooks/useFetch';
import api from '../../../../config/axiosConfig';
import RTL from '../../../../utils/RTL';

const API_ENDPOINT = '/institutes/api/v1/teacher-working-days/email-preferences/';

function EmailManagement() {
    const theme = useTheme();
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isSaving, setIsSaving] = useState(false);

    // Fetch current email preferences
    const { data: emailPreferences, loading, error, refetch } = useFetch(API_ENDPOINT);

    // Initialize local state with fetched data or defaults
    const [preferences, setPreferences] = useState({
        email_daily_report: true,
        email_weekly_report: true,
        email_monthly_report: true,
        email_teacher_schedule_next_day: true,
        email_teacher_monthly_income: true,
        email_student_schedule: true,
        email_student_registration_reminder: true,
    });

    // Update local state when data is fetched
    React.useEffect(() => {
        if (emailPreferences) {
            setPreferences(emailPreferences);
        }
    }, [emailPreferences]);

    const handleToggle = useCallback(async (field) => {
        const newValue = !preferences[field];
        const updatedPreferences = { ...preferences, [field]: newValue };
        
        // Optimistic update
        setPreferences(updatedPreferences);
        setIsSaving(true);

        try {
            await api.patch(API_ENDPOINT, { [field]: newValue });
            setSnackbar({
                open: true,
                message: 'تنظیمات با موفقیت به‌روزرسانی شد.',
                severity: 'success',
            });
        } catch (error) {
            // Revert on error
            setPreferences(preferences);
            const errorMessage = error?.response?.data?.detail 
                || error?.response?.data?.error 
                || 'خطا در به‌روزرسانی تنظیمات';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setIsSaving(false);
        }
    }, [preferences]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {error || 'خطا در بارگذاری تنظیمات ایمیل'}
                </Alert>
            </Box>
        );
    }

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                    مدیریت ارسال ایمیل‌ها
                </Typography>

                <Stack spacing={3}>
                    {/* Management Reports Section */}
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <AssessmentIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                گزارش‌های مدیریت
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Stack spacing={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preferences.email_daily_report}
                                        onChange={() => handleToggle('email_daily_report')}
                                        disabled={isSaving}
                                    />
                                }
                                label="گزارش روزانه به مدیریت"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preferences.email_weekly_report}
                                        onChange={() => handleToggle('email_weekly_report')}
                                        disabled={isSaving}
                                    />
                                }
                                label="گزارش هفتگی به مدیریت"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preferences.email_monthly_report}
                                        onChange={() => handleToggle('email_monthly_report')}
                                        disabled={isSaving}
                                    />
                                }
                                label="گزارش ماهانه به مدیریت"
                            />
                        </Stack>
                    </Paper>

                    {/* Teacher Notifications Section */}
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <PersonIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                اطلاع‌رسانی به مدرسین
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Stack spacing={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preferences.email_teacher_schedule_next_day}
                                        onChange={() => handleToggle('email_teacher_schedule_next_day')}
                                        disabled={isSaving}
                                    />
                                }
                                label="ایمیل برنامه روز بعد به مدرسین"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preferences.email_teacher_monthly_income}
                                        onChange={() => handleToggle('email_teacher_monthly_income')}
                                        disabled={isSaving}
                                    />
                                }
                                label="ایمیل درآمد ماهانه به مدرسین"
                            />
                        </Stack>
                    </Paper>

                    {/* Student Notifications Section */}
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <SchoolIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                اطلاع‌رسانی به هنرجویان
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Stack spacing={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preferences.email_student_schedule}
                                        onChange={() => handleToggle('email_student_schedule')}
                                        disabled={isSaving}
                                    />
                                }
                                label="ایمیل برنامه به هنرجویان"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preferences.email_student_registration_reminder}
                                        onChange={() => handleToggle('email_student_registration_reminder')}
                                        disabled={isSaving}
                                    />
                                }
                                label="یادآوری ثبت‌نام به هنرجویان"
                            />
                        </Stack>
                    </Paper>
                </Stack>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={handleCloseSnackbar} 
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </RTL>
    );
}

export default EmailManagement;

